import * as RadixTooltip from '@radix-ui/react-tooltip';
import { isValidElement, type ReactElement, type TouchEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@hrworks/design-system/hooks';
import { detectOS } from '@hrworks/sui-shared/functions';
import type { FloatDirection } from '@hrworks/sui-shared/types';

import { Text } from '../Text';
import { Title } from '../Title';
import { S } from './Tooltip.styles';
import type { TooltipProps } from './Tooltip.types';

const LONG_PRESS_DURATION = 500;

type RadixSide = 'top' | 'bottom' | 'left' | 'right';
type RadixAlign = 'start' | 'center' | 'end';

const mapPlacement = (placement: FloatDirection): { side: RadixSide; align: RadixAlign } => {
  const parts = placement.split('-');
  const side = parts[0] as RadixSide;
  const align = (parts[1] as RadixAlign) || 'center';
  return { side, align };
};

// On iOS numbers were interpreted as phone numbers and added an underline and broke the layout. This prevents this behaviour. See: FE-1835
const avoidNumberAutoDetectionOnSafari = (text: string) => {
  if (detectOS() !== 'iOS' || !text) {
    return text;
  }
  return text.replaceAll(/(\d)/g, '$1\u200B');
};

export const Tooltip = ({
  children,
  placement = 'top',
  text,
  title,
  mainAxisOffset = 7,
  customTooltip,
  width,
  trigger = 'longHoverOrLongTouch',
  ...otherProps
}: TooltipProps) => {
  const [open, setOpen] = useState(false);
  const touchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isTouchDevice = useMediaQuery('isTouchDevice');

  const handleTouchStart = useCallback(() => {
    if (trigger === 'longHover') return;

    if (trigger === 'hoverOrTouch') {
      setOpen(true);
      return;
    }

    // Open after long press
    touchTimerRef.current = setTimeout(() => {
      setOpen(true);
      touchTimerRef.current = null;
    }, LONG_PRESS_DURATION);
  }, [trigger]);

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (trigger === 'longHover') return;

      if (touchTimerRef.current) {
        // Long-press not yet triggered → clear timer
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = null;
      } else if (trigger === 'longHoverOrLongTouch') {
        // Long-press triggered → prevent synthetic click
        if (event.cancelable) {
          event.preventDefault();
        }
      }

      // For hoverOrTouch: close on touch end
      if (trigger === 'hoverOrTouch') {
        setOpen(false);
      }
    },
    [trigger],
  );

  // Touch-close mechanism: close tooltip when tapping outside trigger
  useEffect(() => {
    if (!isTouchDevice || trigger === 'longHover' || !open) return;

    const handleDocumentTouchStart = (event: globalThis.TouchEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('touchstart', handleDocumentTouchStart);
    return () => {
      document.removeEventListener('touchstart', handleDocumentTouchStart);
    };
  }, [isTouchDevice, trigger, open]);

  // Early return: no tooltip content → render children only
  if (!title && !text && !customTooltip) {
    return <>{children}</>;
  }

  const { side, align } = mapPlacement(placement);

  // Determine delay based on trigger type
  const getDelayDuration = () => {
    switch (trigger) {
      case 'hoverOrTouch':
        return 0;
      case 'longHover':
      case 'longHoverOrLongTouch':
        return 500;
    }
  };

  const disableHoverableContent = trigger === 'longHover';

  // Ensure children is a valid single React element for asChild
  const ensureSingleElement = (): ReactElement<Record<string, unknown>> => {
    if (isValidElement(children)) {
      return children as ReactElement<Record<string, unknown>>;
    }
    // Fallback: wrap in a span (e.g. for strings, numbers, or multiple children)
    // inline-flex ensures the span fits exactly around its content for correct Radix positioning
    return (
      // biome-ignore lint/a11y/noNoninteractiveTabindex: Tooltip trigger must be focusable for keyboard accessibility
      <span tabIndex={0} style={{ display: width === 'full' ? 'flex' : 'inline-flex' }}>
        {children}
      </span>
    );
  };

  const triggerChild = ensureSingleElement();

  // On touch devices, use controlled mode
  const isControlled = isTouchDevice && trigger !== 'longHover';

  const controlledProps = isControlled
    ? {
        open,
        onOpenChange: (newOpen: boolean) => {
          if (!newOpen) {
            setOpen(false);
          }
        },
      }
    : {};

  const tooltipContent = customTooltip || (
    <>
      {title && <Title>{avoidNumberAutoDetectionOnSafari(title)}</Title>}
      {text && (
        <Text typography="bodySm">{typeof text === 'string' ? avoidNumberAutoDetectionOnSafari(text) : text}</Text>
      )}
    </>
  );

  return (
    <RadixTooltip.Provider
      delayDuration={getDelayDuration()}
      skipDelayDuration={300}
      disableHoverableContent={disableHoverableContent}
    >
      <RadixTooltip.Root {...controlledProps}>
        <RadixTooltip.Trigger
          asChild
          // Prevent text selection on long press on mobile devices (especially Safari).
          style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
          ref={triggerRef}
          {...otherProps}
          {...(isTouchDevice &&
            trigger !== 'longHover' && {
              onTouchStart: handleTouchStart,
              onTouchEnd: handleTouchEnd,
            })}
        >
          {triggerChild}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <S.RadixContent
            side={side}
            align={align}
            sideOffset={mainAxisOffset}
            $isCustom={!!customTooltip}
            collisionPadding={8}
          >
            {tooltipContent}
          </S.RadixContent>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
